import { useEffect, useState, useRef } from "react"
import { useDispatch, useSelector } from "react-redux"
import { makeStyles, useTheme } from "@material-ui/core/styles"
import { Box, Grid, IconButton, Typography } from "@material-ui/core"
import { Add as AddIcon, Close as CloseIcon, KeyboardArrowDown } from "@material-ui/icons"
import TableView from "../../components/common/TableView/TableView"
import { getAllDocumentsTableColumns } from "../ViewDocuments/ViewDocumentsUtility"
import TagSelect from "../../components/tags/TagSelect"
import { LIST_VIEW, PAGE_OPTIONS, parsePayload } from "../../utils"
import { useTranslation } from "react-i18next"
import { documentsActions } from "../../actions/documentsActions"
import CreateTag from "../../components/tags/CreateTag"
import EmailDialog from "../../components/common/dialog/Simple"
import DownloadAs from "../../components/common/dialog/DownloadAs"
import ShowAllTags from "../../components/multiTag/ShowAllTags"
import MultiTagView from "../../components/multiTag/MultiTagView"
import { tagButtonClickedReset } from "../../actions/customTagsActions"
import TaggedDate from "../../components/taggedDate/TaggedDate"
import TaggedDateDialog from "../../components/common/dialog/TaggedDateDialog"

export const ListView = (props) => {
	const {
		isEmailType,
		isAllDocsViewSelected,
		page,
		recordsPerPage,
		setPage,
		setRecordsPerPage,
		sortModel,
		setSortModel,
		submitSearch,
		tempSearchText,
		tempCategories,
		filters,
		toRetainFilterLoading = false,
	} = props

	const SERVER_MODE = "server"
	// Translation
	const [t] = useTranslation("global")
	// Theme
	const theme = useTheme()
	/* ******************* REDUX STORE ******************* */
	const dispatch = useDispatch()
	const isAlldocsLoading = useSelector((state) =>
		isAllDocsViewSelected ? state.viewdocuments.allDocumentsLoading : state.viewdocuments.loading
	)
	const conceptsDocLoading = useSelector((state) => state.viewdocuments.loading)
	const allDocumentsCount = useSelector((state) => state.viewdocuments.allDocumentsCount)
	const conceptsDocCount = useSelector((state) => state.viewdocuments.concept_docs.count)
	const bulkDownloadStatus = useSelector((state) => state.downloads.bulk_download)
	const globalStateSearchDocumentList = useSelector((state) => state.timeline.timelineResults)
	const { tagButtonClicked } = useSelector((state) => state.tags)
	const { selectedSearch } = useSelector((state) => state.savedSearch)
	const timeLineLoading = useSelector((state) => state?.timeline?.loading)
	const selectedRowFromRedux = useSelector((state) => state.downloads?.selectedDocumentIds)

	/* ******************* END REDUX STORE ******************* */

	/* ******************* REACT STATES ******************* */

	//Docs to show - Pagination
	const [docsToSHow, setDocsToSHow] = useState({ count: 0, data: [] })
	// Create tag form popup
	const [showTagForm, setShowTagForm] = useState(false)
	//Download Dialog
	const [downloadDialogOpen, setDownloadDialogOpen] = useState(false)
	// Email Dialog
	const [emailDialogOpen, setEmailDialogOpen] = useState(false)
	const [emailDailogData, setEmailDailogData] = useState({})
	// MultiTag Dialog
	const [multiTagDialogOpen, setMultiTagDialogOpen] = useState(false)
	const [multiTagDialogData, setMultiTagDialogData] = useState([])
	const [currFileId, setCurrFileId] = useState(null)
	const [userSelectedList, setUserSelectedList] = useState([])
	const [currentpage, setCurrentPage] = useState(1)
	const [searchDocumentsList, setSearchDocumentslist] = useState({ count: 0, data: [] })
	const [called, setCalled] = useState(false)
	// Tagged date update dialog
	const [taggedDateDialogOpen, setTaggedDateDialogOpen] = useState(false)
	const [tagDatedDialogData, setTagDatedDialogData] = useState({})
	/* ******************* END REACT STATES ******************* */

	// Filtering elements by priority
	useEffect(() => {
		if (globalStateSearchDocumentList.data && globalStateSearchDocumentList.data.length > 0) {
			setDocsToSHow({
				count: globalStateSearchDocumentList?.data.length,
				data: globalStateSearchDocumentList?.data,
			})
			setCalled(true)
		}
	}, [globalStateSearchDocumentList])

	/* ******************* VARIABLES POST INITIALIZATION ******************* */
	const useStyles = makeStyles((theme) => ({
		paper: {
			position: "absolute",
			width: "40%",
			height: "100%",
			backgroundColor: "#9dddd6",
			//border: '2px solid #000',
			boxShadow: theme.shadows[5],
			padding: "16px 15px 24px",
		},
		emailDialogBox: {
			"& p": {
				paddingBottom: 2,
				...theme.typography.body2,
			},
			"& h6": {
				paddingBottom: 4,
				...theme.typography.body2,
				fontSize: "12px",
			},
		},
	}))
	const classes = useStyles()
	const wrapperRef = useRef(null)
	/* ******************* END VARIABLES POST INITIALIZATION ******************* */
	/* ******************* FUNCTION CALL POST INITIALIZATION ******************* */
	const useOutsideAlerter = (ref) => {
		useEffect(() => {
			/**
			 * Alert if clicked on outside of element
			 */
			function handleClickOutside(event) {
				if (ref.current && !ref.current.contains(event.target)) {
					// setSelectIdentify({ show: false })
					// setSelectDateDocument({ show: false })
					setShowTagForm(false)
				}
			}

			// Bind the event listener
			document.addEventListener("mousedown", handleClickOutside)
			return () => {
				// Unbind the event listener on clean up
				document.removeEventListener("mousedown", handleClickOutside)
			}
		}, [ref])
	}

	useOutsideAlerter(wrapperRef)
	/* ******************* END FUNCTION CALL POST INITIALIZATION ******************* */

	const handlePageChange = (newPage) => {
		setPage(newPage)
		const skipCount = newPage === 1 ? 0 : (newPage - 1) * recordsPerPage
		let sort = {}
		if (!!sortModel.length) {
			sort = { field: sortModel[0].field, ordering: sortModel[0].sort === "asc" ? 1 : -1 }
		}
		const refetchGraph = false //will use this flag to check pageNumber change
		submitSearch(tempSearchText, tempCategories, recordsPerPage, skipCount, sort, parsePayload(filters), refetchGraph)
	}

	const handlePageSizeChange = (newPageSize) => {
		setRecordsPerPage(newPageSize)
		setPage(1)
		const skipCount = 0
		let sort = {}
		if (!!sortModel.length) {
			sort = { field: sortModel[0].field, ordering: sortModel[0].sort === "asc" ? 1 : -1 }
		}
		const refetchGraph = false //will use this flag to check pageLimit change
		submitSearch(tempSearchText, tempCategories, newPageSize, skipCount, sort, parsePayload(filters), refetchGraph)
	}

	const handleSortModelChange = (newSortModel) => {
		if (!_.isEmpty(newSortModel)) {
			if (JSON.stringify(newSortModel) !== JSON.stringify(sortModel)) {
				setSortModel(newSortModel)
			}
		}
	}

	const openCreateTag = (e) => {
		setShowTagForm(!showTagForm)
		e.stopPropagation()
	}

	const getTags = (params) => {
		return (
			<MultiTagView
				view={LIST_VIEW}
				fileData={{ id: params?.row?.id, file_name: params?.row?.file_name, tags: params?.row?.tags }}
				tagState={{
					multiTagDialogData,
					multiTagDialogOpen,
					selectedSearch,
					setCurrFileId,
					setMultiTagDialogData,
					setMultiTagDialogOpen,
				}}
			/>
		)
	}

	const renderEmailDialog = (params) => {
		return (
			<Typography
				variant="body2"
				onClick={() => {
					setEmailDialogOpen(true)
					setEmailDailogData({
						to: params?.row?.to,
						cc: params?.row?.cc || [],
						bcc: params?.row?.bcc || [],
					})
				}}
				style={{ cursor: "pointer" }}
			>
				{params?.row?.to?.length > 1 ? (
					<>
						{`${params?.row?.to?.[0]}...`}
						<KeyboardArrowDown style={{ marginLeft: "10px" }} />
					</>
				) : (
					params?.row?.to
				)}
			</Typography>
		)
	}

	const renderTagHeader = () => {
		return (
			<>
				<Typography variant="subtitle2" component="h2">
					{t("tag.title")}
				</Typography>
			</>
		)
	}

	const renderTaggedDate = (params) => {
		return <TaggedDate data={params?.row} dialogData={{ taggedDateDialogOpen, setTaggedDateDialogOpen, setTagDatedDialogData }} />
	}

	const renderPageNumberHeader = () => {
		return (
			<>
				<Typography variant="subtitle2" component="h2" style={{ display: "flex", justifyContent: "end", width: "100%" }}>
					{t("tableColumns.pages")}
				</Typography>
			</>
		)
	}

	const bulkDownloadEvent = () => {
		setDownloadDialogOpen(true)
	}

	const getRows = () => {
		return docsToSHow.count > 0
			? docsToSHow.data.map((docData) => {
					if (docData?.metadata?.source === "email") {
						return {
							...docData,
							id: docData?.file_id,
							subject: docData?.metadata?.subject || "NA",
							title: docData?.metadata?.title || docData?.name || "NA",
							author: docData?.metadata?.from || "NA",
							tags: docData?.tags || "NA",
							tagged_date: docData?.metadata?.tagged_date || "NA",
							view_count: docData?.viewed || 0,
							size: docData?.size || 0,
							comments_count: docData?.comments_count || 0,
							action: "",
							source: docData?.metadata?.source || "NA",
							to: docData?.metadata?.to || "NA",
							from: docData?.metadata?.from || "NA",
							cc: docData?.metadata?.cc || [],
							bcc: docData?.metadata?.bcc || [],
							attachments: docData?.children_files || [],
							reviewed: docData?.reviewed || false,
							pages: docData?.metadata?.total_page_number || 0,
						}
					} else
						return {
							...docData,
							id: docData?.file_id,
							title: docData?.metadata?.title || docData?.name || docData?.metadata?.filename || "NA",
							author: docData?.metadata?.author || "NA",
							tags: docData?.tags || "NA",
							tagged_date: docData?.metadata?.tagged_date || "NA",
							view_count: docData?.viewed || 0,
							size: docData?.size || 0,
							comments_count: docData?.comments_count || 0,
							action: "",
							source: docData?.metadata?.source || "NA",
							attachments: docData?.children_files || [],
							reviewed: docData?.reviewed || false,
							pages: docData?.metadata?.total_page_number || 0,
						}
			  })
			: []
	}

	useEffect(() => {
		dispatch(tagButtonClickedReset())
	}, [])
	const rows = getRows()
	return (
		<>
			<Grid
				container
				item
				className={classes.table}
				justifyContent="space-around"
				style={{ marginTop: "40px", marginBottom: "64px", position: "relative" }}
			>
				{
					<TableView
						columns={getAllDocumentsTableColumns(
							isEmailType,
							renderTagHeader,
							getTags,
							renderEmailDialog,
							() => {},
							rows,
							renderTaggedDate,
							renderPageNumberHeader
						)}
						rows={getRows()}
						isLoading={timeLineLoading}
						pageInfo={{
							paginationMode: SERVER_MODE,
							page: page,
							pageSize: recordsPerPage,
							rowsPerPageOptions: PAGE_OPTIONS,
							totalRowCount: globalStateSearchDocumentList?.count,
							handlePageChange,
							handlePageSizeChange,
						}}
						sortInfo={{
							sortingMode: SERVER_MODE,
							sortModel: sortModel,
							onSortModelChange: handleSortModelChange,
						}}
						checkboxSelection={true}
						footerInfo={{
							hideFooter: false,
							hideSelectionCount: true,
							hidePagination: false,
						}}
						topBarInfo={{
							show: true,
							leftSection: `Total documents: ${globalStateSearchDocumentList?.count}`,
							rightSection: "",
						}}
						totalCountText={`${t("tableColumns.totalFiles")}: ${globalStateSearchDocumentList?.count}`}
						bulkDownloadEvent={bulkDownloadEvent}
						userSelectedRows={setUserSelectedList}
						bulkDownloadStatus={bulkDownloadStatus}
						showDownloadButton={true}
						showColumnsButton={false}
						toApplyHotKeys={!tagButtonClicked}
						showDuplicate={true}
					/>
				}
				{showTagForm && <CreateTag innerRef={wrapperRef} show={showTagForm} />}
			</Grid>
			<EmailDialog
				open={emailDialogOpen}
				title={t("dialog.email.to")}
				onClose={() => {
					setEmailDialogOpen(false)
				}}
			>
				<Box className={classes.emailDialogBox}>
					{emailDailogData?.to?.map((emailTo, index) => {
						return <Typography key={`emailto-${index}`}>{emailTo}</Typography>
					})}
					{emailDailogData?.cc?.length > 0 && <Typography component="h6">{t("dialog.email.cc")}</Typography>}
					{emailDailogData?.cc?.map((cc, index) => {
						return <Typography key={`cc-${index}`}>{cc}</Typography>
					})}
					{emailDailogData?.bcc?.length > 0 && <Typography component="h6">{t("dialog.email.bcc")}</Typography>}
					{emailDailogData?.bcc?.map((bcc, index) => {
						return <Typography key={`bcc-${index}`}>{bcc}</Typography>
					})}
				</Box>
			</EmailDialog>
			<ShowAllTags
				tagState={{
					multiTagDialogData,
					multiTagDialogOpen,
					currFileId,
					selectedSearch,
					setMultiTagDialogData,
					setMultiTagDialogOpen,
					setCurrFileId,
				}}
			/>
			<DownloadAs
				open={downloadDialogOpen}
				title={t("downloadDialog.title")}
				handleBulkDownload={(source) => {
					const ids = Object.keys(selectedRowFromRedux)
					dispatch(documentsActions?.downloadMultipleFileAction(ids, source))
				}}
				onClose={() => {
					setDownloadDialogOpen(false)
				}}
			/>
			<TaggedDateDialog
				open={taggedDateDialogOpen}
				title={t("taggedDateDialog.title")}
				onClose={() => {
					setTaggedDateDialogOpen(false)
				}}
				tagDatedDialogData={tagDatedDialogData}
			/>
		</>
	)
}
